import { format } from 'date-fns';
import { dashboardsMock } from '@/mocks';
import DEFAULT_OPERATIONS from './user.gql';
import { useLazyQuery } from '@apollo/client';
import { CustomerDataProps, DashboardsProps } from '@/types';
import { CheckoutPageProps } from '@/talons/checkoutPage/type';
import { getOnStorage, setOnStorage, updateOnStorage, removeFromStorage } from '@/shared';
import { useState, useEffect, useContext, useCallback, createContext, useRef } from 'react';
import { useClient, useFetchAxios, useGlobals, useNetworkStatusContext, useTables } from '../index';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

interface UserContextProps {
  email: string;
  taxVat: string;
  getCustomer: any;
  valorMinimo: number;
  userToken: string;
  isSignedIn: boolean;
  dashboards: DashboardsProps;
  customer: CustomerDataProps;
  rcaSaldoFlex: number | null;

  signOut: () => void;
  fetchSaldoFlex: () => void;
  setEmail: (value: string) => void;
  setTaxVat?: (value: string) => void;
  setUserToken?: (value: string) => void;
  setIsSignedIn: (value: boolean) => void;
  setRcaSaldoFlex?: (value: number) => void;
  setCustomer: (value: CustomerDataProps) => void;
}

const UserContext = createContext<UserContextProps>({} as UserContextProps);

const CHECK_INTERVAL = 5 * 60 * 1000; // 10 minuto em millisegundos
const ENABLE_INTERVAL = 10 * 60 * 1000; // 5 minutos em millisegundos

const UserProvider: React.FC<any> = ({ children }) => {
  // CONSTANTS
  const { getCustomerQuery } = DEFAULT_OPERATIONS;

  // CONTEXTS
  const { tables } = useTables();
  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();
  const { selectedClient } = useClient();
  const { connectivityStatus } = useNetworkStatusContext();

  // FUNCTIONS
  const updateLastSendTime = () => {
    setOnStorage('last_order_send_time', Date.now());
  };

  const canSendOrders = () => {
    const lastSendTime = getOnStorage('last_order_send_time');
    const now = Date.now();

    if (!lastSendTime) {
      updateLastSendTime();
      return true;
    }

    // Verifica se passou 2 minutos desde o último envio
    const enable = now - lastSendTime >= ENABLE_INTERVAL;
    console.info(`Verificação janela de tempo para enviar pedidos ${format(now, "HH'h' mm'm' ss's'")} :`, enable);
    return enable;
  };

  // REFERENCES
  const refTimer = useRef<NodeJS.Timeout | null>(null);
  const refSendingOrders = useRef<Record<string, boolean>>({});

  // STATES
  const [userToken] = useState('');
  const [email, setEmail] = useState('');
  const [taxVat, setTaxVat] = useState('');
  const [valorMinimo, setValorMinimo] = useState(0);
  const [dashboards] = useState(dashboardsMock as DashboardsProps);
  const [rcaSaldoFlex, setRcaSaldoFlex] = useState<number | null>(() => {
    const rcaCustomer = getOnStorage('rca_usuario');
    return rcaCustomer?.saldoFlex || null;
  });
  const [customer, setCustomer] = useState<CustomerDataProps>(() => {
    const rcaCustomer = getOnStorage('rca_usuario');
    if (rcaCustomer) return rcaCustomer;
    return {} as CustomerDataProps;
  });
  const [isSignedIn, setIsSignedIn] = useState(() => {
    const token = !!getOnStorage('token_magento');
    const rcaCustomer = !!getOnStorage('rca_usuario');
    return token && rcaCustomer;
  });

  // QUERIES
  const [getCustomer, { error: errorCustomer, loading: loadingCustomer }] = useLazyQuery(getCustomerQuery);

  // CALLBACKS
  const getParamsAWS = useCallback(async () => {
    const parametrosAws = tables.current.parametros_aws;

    const vlrMin = parametrosAws?.find(
      (p) => p.desc_parametro === 'VLR_MINIMO_PEDIDO'
    )?.valor[0];
    setValorMinimo(vlrMin ? Number(vlrMin) : 0);
  }, [setValorMinimo]);

  const processOfflineOrders = useCallback(
    async (initialOfflineOrders: CheckoutPageProps[]) => {
      if (window.location.pathname.includes('login')) return;
      // Trabalhar em uma cópia para evitar problemas de índice enquanto removemos itens
      let queue = [...initialOfflineOrders];

      // Marcar localmente como "processando" para feedback imediato
      queue = queue.map((order: any) => ({
        ...order,
        orders: { ...order.orders, status: 'processando' },
      }));
      setOnStorage('offline_orders', queue);

      for (let i = 0; i < queue.length; i++) {
        const order = queue[i];
        if (!!refSendingOrders.current[order.orders.order_number]) continue;
        refSendingOrders.current[order.orders.order_number] = true;
        try {
          const ok = await axiosCtx
            .post('/integra-pedido', {
              ...order,
              origin_function: 'offline',
              current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
              version: version || document.getElementById('version-id')?.textContent || '',
            })
            .then((res) => Boolean(res?.data?.message))
            .catch((error) => {
              console.error('Falha ao enviar pedido offline:', error);
              return false;
            });

          if (!ok) {
            // mantém na fila; segue para o próximo
            continue;
          }

          // Sucesso: mover da fila para "synched_orders"
          let synched = await getOnStorage('synched_orders');
          if (!Array.isArray(synched)) synched = [];
          synched.push(order);
          setOnStorage('synched_orders', synched);

          // Remove da fila e persiste estado
          const remaining = queue.filter((_, idx) => idx !== i);
          setOnStorage('offline_orders', remaining);

          // efeitos colaterais
          const skus = order?.orders?.actions_pending?.map((i: any) => Number(i.sku)) || [];

          // Limpa o flag de envio após sucesso
          delete refSendingOrders.current[order.orders.order_number];

          // Ajusta o índice após remover
          queue = remaining;
          i -= 1;
        } catch (err) {
          console.error('Erro inesperado ao processar pedido offline:', err);
          // Limpa o flag de envio em caso de erro para permitir nova tentativa
          delete refSendingOrders.current[order.orders.order_number];
          // não remove da fila; tentará novamente no próximo ciclo online
        }
      }
    },
    [axiosCtx, selectedClient?.COD_CLIENTE, version]
  );

  const rescueStuckSynchedOrders = useCallback(async () => {
    if (connectivityStatus !== 'online' || window.location.pathname.includes('login')) return;

    // Verificar se pode enviar pedidos (throttling de 2 minutos)
    if (!canSendOrders()) return;
    updateLastSendTime();

    let synched = await getOnStorage('synched_orders');
    if (!Array.isArray(synched) || synched.length === 0) return;

    // Limpeza defensiva de entradas inválidas
    synched = synched.filter((o: any) => o && o.orders && o.orders.order_number);
    setOnStorage('synched_orders', synched);

    // Busca pedidos atuais no servidor para evitar duplicidade
    const rca = getOnStorage('rca_usuario');
    const codRca = rca?.codPessoa;
    let serverIds = new Set<string>();
    try {
      if (codRca) {
        const base = (window as any)?.agp_config?.REACT_APP_AWS;
        if (base) {
          const pedidos = await axiosCtx
            .get(`${base}/pedido-notifica-get?seqpessoa=${codRca}`)
            .then((res) => res?.data?.pedidos || [])
            .catch(() => []);

          serverIds = new Set(
            pedidos.map((p: any) => p?.orders?.order_number_origem || p?.orders?.order_number_origin || p?.orders?.order_number)
          );
        }
      }
    } catch (e) {
      console.warn('Falha ao consultar pedidos no servidor para resgate automático.', e);
    }

    // Remove localmente os que já apareceram no servidor
    const remaining = synched.filter((o: any) => !serverIds.has(o?.orders?.order_number));
    if (remaining.length !== synched.length) {
      setOnStorage('synched_orders', remaining);
    }
  }, [axiosCtx, connectivityStatus, version]);

  const signOut = useCallback(async () => {
    if (connectivityStatus !== 'online') {
      const tokenOffline = getOnStorage('token_magento');
      if (tokenOffline) {
        await Promise.all([setOnStorage('token_offline', tokenOffline), removeFromStorage('token_magento')]);
      }
    } else {
      await Promise.all([
        removeFromStorage('login_date'),
        removeFromStorage('token_magento'),
        removeFromStorage('token_aws'),
        removeFromStorage('rca'),
        removeFromStorage('selected_client'),
        removeFromStorage('selected_rca'),
        removeFromStorage('new_data_sets'),
        removeFromStorage('discounts'),
        removeFromStorage('rca_usuario'),
        localStorage.removeItem('@RCA_APP:selected_client'),
        localStorage.removeItem('localTime'),
        // limpa flag de bootstrap de login, mas datasets persistem normalmente
        localStorage.removeItem('login_bootstrap'),
      ]);

      setEmail('');
      setTaxVat('');
      setRcaSaldoFlex(null);
      setCustomer({} as CustomerDataProps);
    }

    setIsSignedIn(false);
    if (connectivityStatus !== 'offline') unregisterServiceWorker({ preserveLocalStorage: true });
    window.location.reload();
  }, [connectivityStatus]);

  // EFFECTS
  // Busca os parâmetros AWS
  useEffect(() => {
    getParamsAWS();
  }, [getParamsAWS]);

  // A cada 1 minutos, faz a validação de pedidos offline/synched_orders e processa se houver caso tenha dado o intervalo de 2 minutos para enviar novamente
  useEffect(() => {
    if (connectivityStatus !== 'online') {
      if (refTimer.current) clearTimeout(refTimer.current);
      return;
    }

    if (!!refTimer.current) return;

    refTimer.current = setInterval(() => {
      if (!canSendOrders()) return;

      const offlineOrders = getOnStorage('offline_orders') as CheckoutPageProps[];
      if (offlineOrders?.length) {
        processOfflineOrders(offlineOrders);
      }
      rescueStuckSynchedOrders();
      // A cada 1 minutos, faz a validação de pedidos offline e processa se houver caso tenha dado o tempo de 10 minutos para enviar novamente
    }, CHECK_INTERVAL);
  }, [connectivityStatus, rescueStuckSynchedOrders, processOfflineOrders]);

  // Valida se o usuário está logado e online, caso sim processa os pedidos offline
  useEffect(() => {
    if (connectivityStatus !== 'online' || !isSignedIn) return;

    const offlineOrders = getOnStorage('offline_orders') as CheckoutPageProps[];
    if (offlineOrders?.length) processOfflineOrders(offlineOrders);
    rescueStuckSynchedOrders();
  }, [connectivityStatus, isSignedIn, rescueStuckSynchedOrders, processOfflineOrders]);

  const fetchSaldoFlex = async () => {
    const rcaCustomer = getOnStorage('rca_usuario');
    if (!rcaCustomer) return;

    const fetchedSaldoFlex = tables.current.saldo_flex;

    const codPessoa = rcaCustomer?.codPessoa;
    if (!codPessoa) return;

    const parseNumberLoose = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
        const num = Number(normalized);
        return Number.isFinite(num) ? num : null;
      }
      return null;
    };

    // Novo formato: { [COD_PESSOA]: { VLR_ERP } }
    const node = fetchedSaldoFlex?.[String(codPessoa)] ?? null;
    const saldoERPValue = node?.VLR_ERP ?? null;
    const finalValue = parseNumberLoose(saldoERPValue);

    if (finalValue !== null) {
      const updatedCustomer = {
        ...rcaCustomer,
        saldoFlex: finalValue,
        saldoERP: finalValue,
      };
      updateOnStorage('rca_usuario', updatedCustomer);
      setRcaSaldoFlex(finalValue);
      setCustomer(updatedCustomer);
    }
  };

  // Valida se o usuário está logado e valida o saldo flex atualiando o saldo flex do usuário
  useEffect(() => {
    const token = getOnStorage('token_magento');
    const rcaCustomer = getOnStorage('rca_usuario');
    const signInStatus = !!(token && rcaCustomer);

    if (signInStatus !== isSignedIn) setIsSignedIn(signInStatus);
    if (!customer && rcaCustomer && signInStatus) setCustomer(rcaCustomer || ({} as CustomerDataProps));

    if (signInStatus && rcaSaldoFlex === null) {
      fetchSaldoFlex();
    }
  }, [isSignedIn]);

  // Atualiza o saldo flex do usuário
  useEffect(() => {
    if (rcaSaldoFlex !== null) {
      const rcaCustomer = getOnStorage('rca_usuario');
      if (rcaCustomer) {
        updateOnStorage('rca_usuario', {
          ...rcaCustomer,
          saldoFlex: rcaSaldoFlex,
        });
      }
    }
  }, [rcaSaldoFlex]);

  // Valida se o usuário está logado
  useEffect(() => {
    const token = !!getOnStorage('token_magento');
    const rcaCustomer = !!getOnStorage('rca_usuario');
    setIsSignedIn(token && rcaCustomer);
  }, []);

  // Se não tiver customer, desloga o usuário
  useEffect(() => {
    if (!customer) signOut();
  }, [errorCustomer, loadingCustomer, customer, signOut]);

  return (
    <UserContext.Provider
      value={{
        email,
        taxVat,
        customer,
        userToken,
        isSignedIn,
        dashboards,
        valorMinimo,
        rcaSaldoFlex,

        signOut,
        setEmail,
        setTaxVat,
        setIsSignedIn,
        getCustomer,
        setCustomer,
        setRcaSaldoFlex,
        fetchSaldoFlex,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export { UserProvider, useUserContext };
